import type { FinalJob } from '../lambda/types';
import type { DisplayJob } from '../types';
import { replaceMaria01ForLocale, replaceMaria01LocalizedInJobLocaleLocationArrays } from '../geocoding/replace-maria01-location';
import { coerceStringArrayField } from '../seo/coerce-string-array-field';
import { resolveJobDisplayDescriptionFromLocaleRecord } from '../job/pick-job-display-description';

type SchemaJobLocation = {
  addressLocality?: string;
  addressRegion?: string;
  addressCountry?: string;
};

export type JobDetailNormalizationOptions = {
  locale: string;
  seoSlug?: string;
};

export type JobDetailNormalizationResult = {
  displayJob: DisplayJob;
  canonicalJob: FinalJob;
  schemaOverrides: {
    jobLocationType?: 'ON_SITE' | 'HYBRID' | 'TELECOMMUTE';
    jobLocations?: SchemaJobLocation[];
    applicantLocationRequirements?: Record<string, unknown> | Record<string, unknown>[];
    disableSalary?: boolean;
  };
  visibleText: string;
  notes: string[];
};

const HELSINKI_LABELS: Record<string, string> = {
  en: 'Helsinki, Finland',
  fi: 'Helsinki, Suomi',
  sv: 'Helsingfors, Finland',
};

const REMOTE_LOCATION_LABELS: Record<string, string> = {
  en: 'Remote - Finland',
  fi: 'Etätyö - Suomi',
  sv: 'Distans - Finland',
};

const REMOTE_REMOTENESS_LABELS: Record<string, string> = {
  en: 'Remote in Finland',
  fi: 'Etätyö Suomessa',
  sv: 'Distansarbete i Finland',
};

const HYBRID_REMOTENESS_LABELS: Record<string, string> = {
  en: 'Hybrid (Helsinki office)',
  fi: 'Hybridi (Helsinki-toimisto)',
  sv: 'Hybrid (Helsingfors-kontor)',
};

function cloneDisplayJob(job: DisplayJob): DisplayJob {
  const cloned: DisplayJob = { ...job };
  if (Array.isArray(job.location)) {
    cloned.location = [...job.location];
  }
  if (Array.isArray(job.requirements)) {
    cloned.requirements = [...job.requirements];
  }
  if (Array.isArray(job.responsibilities)) {
    cloned.responsibilities = [...job.responsibilities];
  }
  const skillsAndTechnologies = coerceStringArrayField(job.skillsAndTechnologies);
  if (skillsAndTechnologies.length > 0) {
    cloned.skillsAndTechnologies = skillsAndTechnologies;
  }
  if (Array.isArray(job.specialisations)) {
    cloned.specialisations = [...job.specialisations];
  }
  const requiredLanguages = coerceStringArrayField(job.requiredLanguages);
  if (requiredLanguages.length > 0) {
    cloned.requiredLanguages = requiredLanguages;
  }
  const optionalLanguages = coerceStringArrayField(job.optionalLanguages);
  if (optionalLanguages.length > 0) {
    cloned.optionalLanguages = optionalLanguages;
  }
  if (Array.isArray(job.benefits)) {
    cloned.benefits = [...job.benefits];
  }
  if (Array.isArray(job.recruitmentProcess)) {
    cloned.recruitmentProcess = [...job.recruitmentProcess];
  }
  if (Array.isArray(job.perks)) {
    cloned.perks = [...job.perks];
  }
  if (Array.isArray(job.companyCoreValues)) {
    cloned.companyCoreValues = [...job.companyCoreValues];
  }
  if (Array.isArray(job.companyAwardsAndCertifications)) {
    cloned.companyAwardsAndCertifications = [...job.companyAwardsAndCertifications];
  }
  return cloned;
}

function cloneFinalJob(job: FinalJob): FinalJob {
  const cloned: FinalJob = { ...job };
  const cloneLocaleArray = (key: 'location_en' | 'location_fi' | 'location_sv') => {
    const value = job[key];
    if (Array.isArray(value)) {
      cloned[key] = [...value];
    }
  };
  cloneLocaleArray('location_en');
  cloneLocaleArray('location_fi');
  cloneLocaleArray('location_sv');
  return cloned;
}

function normalizeLocationsArray(value: DisplayJob['location']): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter((entry) => entry.length > 0);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  return [];
}

function ensureUnique(list: string[], value: string): string[] {
  const lower = value.toLowerCase();
  const existing = list.find((entry) => entry.toLowerCase() === lower);
  if (existing) return list;
  return [value, ...list];
}

function gatherVisibleText(job: DisplayJob): string {
  const fragments: string[] = [];
  const push = (val: unknown) => {
    if (!val) return;
    if (typeof val === 'string') {
      fragments.push(val);
    } else if (Array.isArray(val)) {
      val.forEach((entry) => {
        if (typeof entry === 'string') {
          fragments.push(entry);
        }
      });
    }
  };

  // originalDescription is the field the page actually renders (pick-job-display-description
  // prefers it over the derived rewrite) — leaving it out made checkSalaryVisibility suppress
  // baseSalary for any job whose wage text only appears in the faithful employer extract.
  push(job.originalDescription);
  push(job.description);
  push(job.requirements);
  push(job.responsibilities);
  push(job.skillsAndTechnologies);
  push(job.perks);
  push(job.benefits);
  push(job.recruitmentProcess);
  if (job.salaryDetails) {
    push(job.salaryDetails);
  }
  return fragments.join('\n');
}

function isFullyRemote(job: DisplayJob, visibleText: string): boolean {
  const remoteness = (job.remoteness || '').toLowerCase();
  if (remoteness.includes('hybrid')) return false;
  const text = visibleText.toLowerCase();
  const remoteIndicators = [
    'fully remote',
    '100% remote',
    'remote in finland',
    'work remotely from finland',
    'remote-first',
  ];
  const hasRemoteIndicator =
    remoteness.includes('remote') ||
    remoteIndicators.some((indicator) => text.includes(indicator));
  const locationList = normalizeLocationsArray(job.location).map((entry) =>
    entry.toLowerCase()
  );
  const locationIndicatesRemote = locationList.some(
    (loc) => loc.includes('remote') || loc.includes('anywhere')
  );
  return hasRemoteIndicator && locationIndicatesRemote;
}

function createSchemaLocations(locations: SchemaJobLocation[] | undefined): SchemaJobLocation[] | undefined {
  if (!locations || locations.length === 0) return undefined;
  return locations.map((loc) => ({
    ...(loc.addressLocality ? { addressLocality: loc.addressLocality } : {}),
    ...(loc.addressRegion ? { addressRegion: loc.addressRegion } : {}),
    ...(loc.addressCountry ? { addressCountry: loc.addressCountry } : {}),
  }));
}

export function normalizeJobDetail(
  job: FinalJob,
  displayJob: DisplayJob,
  options: JobDetailNormalizationOptions
): JobDetailNormalizationResult {
  const normalizedDisplayJob = cloneDisplayJob(displayJob);
  // Locale fallback: transformJobForLanguage keeps only the page locale's suffixed fields, so
  // a job translated in another locale only (e.g. originalDescription_en on a /fi/ page)
  // reaches here with no description at all. The visible page already falls back across
  // locales (pick-job-display-description); mirror that here so the JobPosting schema,
  // salary-visibility check, and indexer gating see the same body the page renders.
  if (!normalizedDisplayJob.originalDescription && !normalizedDisplayJob.description) {
    const resolved = resolveJobDisplayDescriptionFromLocaleRecord(
      job as unknown as Record<string, unknown>,
      options.locale
    );
    if (resolved.source === 'original') {
      normalizedDisplayJob.originalDescription = resolved.body;
    } else if (resolved.source === 'derived') {
      normalizedDisplayJob.description = resolved.body;
    }
  }
  const canonicalJob = cloneFinalJob(job);
  replaceMaria01LocalizedInJobLocaleLocationArrays(canonicalJob);
  const schemaOverrides: JobDetailNormalizationResult['schemaOverrides'] = {};
  const visibleText = gatherVisibleText(normalizedDisplayJob);
  const notes: string[] = [];

  const slug = (options.seoSlug || '').toLowerCase();
  const slugSuggestsHybridHelsinki =
    slug.includes('hybrid') && slug.includes('helsinki') && slug.includes('finland');

  const locale = options.locale as keyof typeof HELSINKI_LABELS;
  const helsinkiLabel = HELSINKI_LABELS[locale] ?? HELSINKI_LABELS.en;
  const remoteLocationLabel =
    REMOTE_LOCATION_LABELS[locale] ?? REMOTE_LOCATION_LABELS.en;
  const remoteRemotenessLabel =
    REMOTE_REMOTENESS_LABELS[locale] ?? REMOTE_REMOTENESS_LABELS.en;
  const hybridRemotenessLabel =
    HYBRID_REMOTENESS_LABELS[locale] ?? HYBRID_REMOTENESS_LABELS.en;

  let locationList = normalizeLocationsArray(normalizedDisplayJob.location);
  locationList = locationList.filter((entry, index, arr) => {
    const lower = entry.toLowerCase();
    return (
      lower.length > 0 &&
      index === arr.findIndex((candidate) => candidate.toLowerCase() === lower)
    );
  });

  const displayLocale: 'en' | 'fi' | 'sv' =
    options.locale === 'fi' || options.locale === 'sv' ? options.locale : 'en';
  locationList = locationList.map((entry) =>
    typeof entry === 'string' ? replaceMaria01ForLocale(entry, displayLocale) : entry,
  );
  normalizedDisplayJob.location = locationList;

  const fullyRemote = isFullyRemote(normalizedDisplayJob, visibleText);

  if (slugSuggestsHybridHelsinki) {
    if (fullyRemote) {
      schemaOverrides.jobLocationType = 'TELECOMMUTE';
      schemaOverrides.applicantLocationRequirements = { '@type': 'Country', name: 'Finland' };
      schemaOverrides.jobLocations = undefined;
      locationList = ensureUnique(
        locationList.filter((loc) => !loc.toLowerCase().includes('helsinki')),
        remoteLocationLabel
      );
      normalizedDisplayJob.location = locationList;
      normalizedDisplayJob.remoteness = remoteRemotenessLabel;

      const setLocaleLocation = (localeKey: 'en' | 'fi' | 'sv', value: string) => {
        const key = `location_${localeKey}` as keyof FinalJob;
        const existing = canonicalJob[key];
        const updated = Array.isArray(existing)
          ? ensureUnique(existing as string[], value)
          : [value];
        canonicalJob[key] = updated as any;
      };
      setLocaleLocation('en', REMOTE_LOCATION_LABELS.en);
      setLocaleLocation('fi', REMOTE_LOCATION_LABELS.fi);
      setLocaleLocation('sv', REMOTE_LOCATION_LABELS.sv);
      notes.push('Slug indicates hybrid Helsinki but job appears fully remote; normalizing to TELECOMMUTE.');
    } else {
      schemaOverrides.jobLocations = createSchemaLocations([
        { addressLocality: 'Helsinki', addressCountry: 'FI' },
      ]);
      locationList = ensureUnique(locationList, helsinkiLabel);
      normalizedDisplayJob.location = locationList;
      normalizedDisplayJob.remoteness = hybridRemotenessLabel;

      const setLocaleLocation = (
        localeKey: 'en' | 'fi' | 'sv',
        value: string
      ) => {
        const key = `location_${localeKey}` as keyof FinalJob;
        const existing = canonicalJob[key];
        const updated = Array.isArray(existing)
          ? ensureUnique(existing as string[], value)
          : [value];
        canonicalJob[key] = updated as any;
      };
      setLocaleLocation('en', HELSINKI_LABELS.en);
      setLocaleLocation('fi', HELSINKI_LABELS.fi);
      setLocaleLocation('sv', HELSINKI_LABELS.sv);
      notes.push('Slug indicates hybrid Helsinki; enforcing Helsinki office in schema.');
    }
  } else {
    normalizedDisplayJob.location = locationList;
  }

  // Salary visibility heuristics: require both min/max values to appear in visible text.
  const checkSalaryVisibility = (): boolean => {
    const { salaryRangeMin, salaryRangeMax } = normalizedDisplayJob;
    if (
      typeof salaryRangeMin !== 'number' ||
      Number.isNaN(salaryRangeMin) ||
      salaryRangeMin <= 0
    ) {
      return false;
    }
    if (
      typeof salaryRangeMax !== 'number' ||
      Number.isNaN(salaryRangeMax) ||
      salaryRangeMax <= 0
    ) {
      return false;
    }
    if (!normalizedDisplayJob.salaryPeriod) {
      return false;
    }
    // Nordic job postings commonly group thousands with a non-breaking or thin space
    // (e.g. "3 500 €" using U+00A0/U+202F/U+2009) rather than a plain space; normalize
    // all of those to a plain space so verbatim matching doesn't false-negative on typography.
    const normalizeWhitespace = (value: string) => value.replace(/[  -   ]/g, ' ');
    const text = normalizeWhitespace(visibleText.toLowerCase());
    const valuePatterns = (value: number) => {
      const raw = Math.round(value).toString();
      const spaced = raw.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      const dotted = raw.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const comma = raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return Array.from(new Set([raw, spaced, dotted, comma]));
    };

    const containsValue = (patterns: string[]) =>
      patterns.some((pattern) => text.includes(normalizeWhitespace(pattern.toLowerCase())));

    const minMatches = containsValue(valuePatterns(salaryRangeMin));
    const maxMatches = containsValue(valuePatterns(salaryRangeMax));
    if (!minMatches || !maxMatches) {
      return false;
    }
    return true;
  };

  const salaryVisible = checkSalaryVisibility();
  if (!salaryVisible) {
    schemaOverrides.disableSalary = true;
    notes.push('Salary values not visibly confirmed; omitting salary from structured data.');
  }

  return {
    displayJob: normalizedDisplayJob,
    canonicalJob,
    schemaOverrides,
    visibleText,
    notes,
  };
}

